<template>
    <LimitedWidthLayout>
        <ParallaxImage
            v-if="pageData.imageSrc"
            :src="pageData.imageSrc"
            :alt="pageData.imageAlt"
            :class="$style.image_override"
        />
        <Typography
            :is="'div'"
            type="paragraph"
            size="m"
            :class="$style.markdownWrapper"
        >
            <component :is="markup" />
        </Typography>
    </LimitedWidthLayout>
</template>
<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { RuleType as MdRuleType, compiler as md } from 'markdown-to-jsx'
import { computed, h } from 'vue'
import { useRoute } from 'vue-router'

import ContactFormForMarkdown from '@/components_new/ContactForm/ContactFormForMarkdown.vue'
import Link from '@/components_new/Link.vue'
import ParallaxImage from '@/components_new/ParallaxImage/ParallaxImage.vue'
import Typography from '@/components_new/Typography.vue'
import { useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import { useI18nAddon } from '@/i18n/i18n'
import LimitedWidthLayout from '@/layouts/LimitedWidthLayout.vue'
import { ROUTE_NAMES } from '@/router/constants'

import aboutUs from './about-us/About us.md?raw'
import privacy from './confidential/Privacy Policy.md?raw'
import digitalServiceAct from './digital-service-act/digital-service-act.md?raw'
import boringDocsDisk from './images/boring-docs-disk.webp?file'
import gemsOnPillow from './images/gems-on-pillow.webp?file'
import halvedApple from './images/halved-apple.webp?file'
import personInAFlowerField from './images/person-in-a-flower-field.webp?file'
import pingPongRocket from './images/ping-pong-rocket.webp?file'
import license from './license/License agreement.md?raw'
import partners from './partners-api/partners-api.md?raw'
import takedownNotice from './takedown-notice/takedown-notice.md?raw'
import termsofuseMcm from './termsofuse-mcm/Terms of Use MCM.md?raw'
import termsofuse from './termsofuse/Terms of Use.md?raw'
import wlOppoAboutUs from './wl-oppo/about-us/About Us.md?raw'
import wlOppoPrivacy from './wl-oppo/confidential/Privacy Policy.md?raw'
import wlOppoContactUs from './wl-oppo/contact-us/Contact Us.md?raw'
import wlOppoTermsOfUse from './wl-oppo/termsofuse/Terms of Use.md?raw'

const tr = useTr()
const route = useRoute()

const { Organization, WebSite, getInLanguage } = useSchemaOrg()
const defaultLocale = useI18nAddon().getDefaultLocale()

const pageData = computed(() => {
    if (route.name === ROUTE_NAMES.WHITE_LABEL_CONFIDENTIAL) {
        return {
            src: wlOppoPrivacy,
            pageMeta: {
                title: `Privacy policy`,
                meta: [
                    {
                        name: 'description',
                        content:
                            'Commitment to protecting your privacy and personal information. Read our privacy policy.',
                    },
                ],
            },
        }
    }
    if (route.name === ROUTE_NAMES.WHITE_LABEL_ABOUT_US) {
        return {
            src: wlOppoAboutUs,
            pageMeta: {
                title: 'Best Web Games | About us',
                meta: [
                    {
                        name: 'description',
                        content: 'Best Web Games — your daily destination for free online gaming.',
                    },
                ],
            },
        }
    }

    if (route.name === ROUTE_NAMES.WHITE_LABEL_CONTACTS) {
        return {
            src: wlOppoContactUs,
            pageMeta: {
                title: 'Contact Us',
            },
        }
    }

    if (route.name === ROUTE_NAMES.WHITE_LABEL_TERMS_OF_USE) {
        return {
            src: wlOppoTermsOfUse,
            pageMeta: {
                title: 'Terms of Use',
            },
        }
    }

    if (route.name === ROUTE_NAMES.CONFIDENTIAL) {
        return {
            src: privacy,
            imageSrc: personInAFlowerField,
            imageAlt: tr('staticPage.person-in-a-flower-field-image-alt'),
            pageMeta: {
                title: `${tr('staticPage.privacyMetaTitle')} 🔒`,
                meta: [
                    {
                        name: 'description',
                        content: tr('staticPage.privacyMetaDescription'),
                    },
                ],
            },
        }
    }
    if (route.name === ROUTE_NAMES.TERMS_OF_USE) {
        return {
            src: termsofuse,
            imageSrc: pingPongRocket,
            imageAlt: tr('staticPage.ping-pong-rocket-image-alt'),
            pageMeta: {
                title: `${tr('staticPage.termsOfUseMetaTitle')} ⚖️`,
                meta: [
                    {
                        name: 'description',
                        content: tr('staticPage.termsOfUsDescription'),
                    },
                ],
            },
        }
    }

    if (route.name === ROUTE_NAMES.TERMS_OF_USE_MCM) {
        return {
            src: termsofuseMcm,
            imageSrc: pingPongRocket,
            imageAlt: tr('staticPage.ping-pong-rocket-image-alt'),
            pageMeta: {
                title: `${tr('staticPage.termsOfUseMetaTitleMcm')} ⚖️`,
                meta: [
                    {
                        name: 'description',
                        content: tr('staticPage.termsOfUsDescriptionMcm'),
                    },
                ],
            },
        }
    }

    if (route.name === ROUTE_NAMES.DIGITAL_SERVICE_ACT) {
        return {
            src: digitalServiceAct,
            pageMeta: {
                title: `${tr('staticPage.digitalServiceActMetaTitle')}`,
                meta: [
                    {
                        name: 'description',
                        content: tr('staticPage.digitalServiceActMetaDescription'),
                    },
                ],
            },
        }
    }

    if (route.name === ROUTE_NAMES.LICENSE) {
        return {
            src: license,
            imageSrc: boringDocsDisk,
            imageAlt: tr('staticPage.boring-docs-disk-image-alt'),
            pageMeta: {
                title: `${tr('staticPage.licenseMetaTitle')} 📄`,
                meta: [
                    {
                        name: 'description',
                        content: tr('staticPage.licenseMetaDescription'),
                    },
                ],
            },
        }
    }
    if (route.name === ROUTE_NAMES.PARTNERS_API) {
        // TODO: нужна ли тут картинка и есть ли дизайн?
        return {
            src: partners,
            imageSrc: gemsOnPillow,
            imageAlt: tr('staticPage.gems-on-pillow-image-alt'),
            pageMeta: {},
        }
    }

    if (route.name === ROUTE_NAMES.TAKEDOWN_NOTICE) {
        return {
            src: takedownNotice,
            imageSrc: halvedApple,
            imageAlt: tr('staticPage.halved-apple-image-alt'),
            pageMeta: {
                title: `${tr('staticPage.takedownNoticeTitle')}`,
                meta: [
                    {
                        name: 'description',
                        content: tr('staticPage.takedownNoticeDescription'),
                    },
                ],
            },
        }
    }

    if (route.name === ROUTE_NAMES.ABOUT_US) {
        const organizationSchema = {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@graph': [
                    Organization.value,
                    WebSite.value,
                    {
                        '@type': 'AboutPage',
                        url: 'https://playgama.com/about',
                        name: 'Playgama',
                        // Do not translate this description, it is used on no-locale page
                        description:
                            "Playgama is a fast-growing platform that connects HTML5 game developers with a global audience of over 300 million monthly players. Whether you're looking to publish, monetize, or scale your game, Playgama gives you the tools and exposure to make it happen.",
                        inLanguage: getInLanguage(defaultLocale),
                    },
                ],
            }),
        }
        return {
            src: aboutUs,
            pageMeta: {
                // Do not translate this title, it is used on no-locale page
                title: 'About Playgama',
                meta: [
                    {
                        name: 'description',
                        // Do not translate this description, it is used on no-locale page
                        content:
                            "Playgama is a fast-growing platform that connects HTML5 game developers with a global audience of over 300 million monthly players. Whether you're looking to publish, monetize, or scale your game, Playgama gives you the tools and exposure to make it happen.",
                    },
                ],
                script: [organizationSchema],
            },
        }
    }

    return { src: '', pageMeta: {} }
})

const pageMeta = computed(() => pageData.value.pageMeta)
const src = computed(() => pageData.value.src)
useHead(pageMeta)

const markup = computed(() =>
    md(src.value, {
        createElement: (tag, rawProps, ...children) => {
            const { className, ...props } = rawProps
            switch (tag) {
                case 'h1': {
                    return h(
                        Typography,
                        {
                            type: 'header',
                            size: 'l',
                            is: tag,
                            ...props,
                        },
                        {
                            default: () => children,
                        },
                    )
                }
                case 'h2':
                case 'h3':
                case 'h4':
                case 'h5':
                case 'h6': {
                    return h(
                        Typography,
                        {
                            type: 'header',
                            size: 's',
                            is: tag,
                            ...props,
                        },
                        {
                            default: () => children,
                        },
                    )
                }
                case 'code': {
                    return h(
                        Typography,
                        {
                            type: 'paragraph',
                            size: 's',
                            is: tag,
                            ...props,
                        },
                        {
                            default: () => children,
                        },
                    )
                }
                case 'a': {
                    return h(
                        Link,
                        {
                            externalTo: props.href,
                            color: 'purple-600',
                            target: props.target || '_blank',
                        },
                        {
                            default: () => children,
                        },
                    )
                }
                case 'contact-form-for-markdown': {
                    return h(
                        ContactFormForMarkdown,
                        { ...props },
                        {
                            default: () => children,
                        },
                    )
                }
                default:
                    return h(tag, props, children)
            }
        },
        // Using renderRule for code blocks only, because need to know original AST node type, to conditionally render inline and block code.
        // TODO maybe get rid of createElement above completely?
        renderRule: (next, node /* , renderChildren, state */) => {
            switch (node.type) {
                case MdRuleType.codeBlock: {
                    return h(
                        'pre',
                        h(
                            Typography,
                            {
                                type: 'paragraph',
                                size: 'xs',
                                is: 'code',
                            },
                            {
                                default: () => node.text,
                            },
                        ),
                    )
                }
                case MdRuleType.codeInline: {
                    return h(
                        Typography,
                        {
                            type: 'paragraph',
                            size: 's',
                            is: 'code',
                        },
                        {
                            default: () => node.text,
                        },
                    )
                }
                default:
                    return next()
            }
        },
    }),
)
</script>
<style module>
.image_override {
    margin-bottom: var(--gap-24);
}
.markdownWrapper p {
    margin-bottom: var(--gap-16);
}

.markdownWrapper li {
    margin-bottom: var(--gap-8);
}

.markdownWrapper li p {
    margin: 0;
}

.markdownWrapper blockquote {
    background-color: rgba(255, 255, 255, 0.1);
    margin: var(--gap-24) 0 var(--gap-24) 0;
    padding: var(--gap-24);
    border-radius: var(--radius-24);
}

.markdownWrapper hr {
    margin: 40px 0;
    border: none;
    border-top: 1px solid var(--white-25);
}

.markdownWrapper code {
    font-family: monospace; /* TODO move to Typography */
    background-color: rgba(255, 255, 255, 0.1);
    display: inline-block;
    padding: 0 5px;
}

.markdownWrapper pre {
    margin: 0;
}

.markdownWrapper pre code {
    padding: var(--gap-24) var(--gap-12);
    display: block;
    border-radius: var(--radius-12);
    white-space: pre-wrap;
    margin-bottom: var(--gap-24);
}

.markdownWrapper h1 {
    padding-bottom: 40px;
    margin-bottom: 40px;
    border-bottom: 1px solid var(--white-16);
    text-align: center;
}

.markdownWrapper h2,
.markdownWrapper h3 {
    margin-top: 40px;
    margin-bottom: var(--gap-24);
}

.markdownWrapper table {
    border-collapse: collapse;
    width: 100%;
    padding: var(--gap-24);
    border-radius: var(--radius-24);
    margin-bottom: var(--gap-24);
}

.markdownWrapper th {
    text-align: left;
}

.markdownWrapper tr td,
.markdownWrapper tr th {
    border-top: 1px solid var(--white-25);
    border-bottom: 1px solid var(--white-25);
    padding: var(--gap-16) var(--gap-8) var(--gap-16) 0;
}

.markdownWrapper tr td:last-child {
    padding-right: 0;
}

.markdownWrapper img {
    max-width: 100%;
}

.markdownWrapper ul {
    list-style-type: disc;
    margin: var(--gap-16) 0;
    padding: 0 var(--gap-24);
    border-radius: var(--radius-24);
}

.markdownWrapper ul li {
    padding: 0;
}

.markdownWrapper ul li ol {
    list-style-type: lower-alpha;
}

.markdownWrapper ul li ol ul {
    list-style-type: circle;
}

.markdownWrapper table ul {
    word-break: break-word;
}

.contactForm_override {
    margin: 48px 0;
}
</style>
